/**
 * PM Payment Requests Component
 * Shows payment requests for a project (PM creates, Owner approves)
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    Grid,
    List,
    ListItem,
    ListItemText,
    Divider,
    IconButton
} from '@mui/material';
import {
    AttachMoney,
    CheckCircle,
    Cancel,
    Pending,
    Receipt,
    PhotoCamera,
    Add,
    Visibility
} from '@mui/icons-material';
import projectManagerApi from '../../services/projectManagerApi';
import photoUploadService from '../../services/photoUploadService';
import { useAuth } from '../../contexts/AuthContext';
import DialogCloseButton from '../common/DialogCloseButton';

const PMPaymentRequests = ({ projectId, isOwner = false }) => {
    const { user } = useAuth();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [newRequest, setNewRequest] = useState({
        amount: '',
        paymentType: 'material_purchase',
        description: '',
        justification: '',
        receipts: [],
        photos: []
    });
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [responseNotes, setResponseNotes] = useState('');

    const handleFileUpload = async (e, field) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            const urls = [];
            for (const f of files) {
                const res = await photoUploadService.uploadPhoto(f);
                if (res?.photoUrl) urls.push(res.photoUrl);
            }
            setNewRequest((prev) => ({ ...prev, [field]: [...prev[field], ...urls] }));
        } catch (err) {
            console.error('File upload failed:', err);
            setError('Upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    useEffect(() => {
        if (projectId) {
            loadPaymentRequests();
        }
    }, [projectId]);

    const loadPaymentRequests = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await projectManagerApi.getPaymentRequests(projectId);
            if (response.success) {
                setRequests(response.data || []);
            }
        } catch (err) {
            console.error('Error loading payment requests:', err);
            setError('Failed to load payment requests');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateRequest = async () => {
        try {
            setSubmitting(true);
            const response = await projectManagerApi.createPaymentRequest({
                projectId,
                ...newRequest,
                amount: parseFloat(newRequest.amount)
            });
            if (response.success) {
                setCreateDialogOpen(false);
                setNewRequest({
                    amount: '',
                    paymentType: 'material_purchase',
                    description: '',
                    justification: '',
                    receipts: [],
                    photos: []
                });
                loadPaymentRequests();
            }
        } catch (err) {
            console.error('Error creating payment request:', err);
            setError(err.message || 'Failed to create payment request');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRespond = async (requestId, action) => {
        try {
            setSubmitting(true);
            const response = await projectManagerApi.respondToPaymentRequest(requestId, action, responseNotes);
            if (response.success) {
                setViewDialogOpen(false);
                setSelectedRequest(null);
                setResponseNotes('');
                loadPaymentRequests();
            }
        } catch (err) {
            console.error('Error responding to payment request:', err);
            setError(err.message || 'Failed to respond to payment request');
        } finally {
            setSubmitting(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved':
            case 'auto_approved':
                return 'success';
            case 'rejected':
                return 'error';
            case 'pending':
                return 'warning';
            default:
                return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'approved':
            case 'auto_approved':
                return <CheckCircle />;
            case 'rejected':
                return <Cancel />;
            case 'pending':
                return <Pending />;
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Payment Requests</Typography>
                {!isOwner && (
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => setCreateDialogOpen(true)}
                    >
                        Create Payment Request
                    </Button>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {requests.length === 0 ? (
                <Alert severity="info">No payment requests yet</Alert>
            ) : (
                <Grid container spacing={2}>
                    {requests.map((request) => (
                        <Grid item xs={12} key={request.id}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box>
                                            <Typography variant="h6">
                                                ${parseFloat(request.amount).toLocaleString()}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {request.payment_type.replace('_', ' ').toUpperCase()}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            icon={getStatusIcon(request.status)}
                                            label={request.status.replace('_', ' ')}
                                            color={getStatusColor(request.status)}
                                            size="small"
                                        />
                                    </Box>

                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Description:</strong> {request.description}
                                    </Typography>
                                    {request.justification && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            <strong>Justification:</strong> {request.justification}
                                        </Typography>
                                    )}

                                    {request.auto_approved && (
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            Auto-approved (within PM authority limits)
                                        </Alert>
                                    )}

                                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                        {request.receipts && request.receipts.length > 0 && (
                                            <Chip icon={<Receipt />} label={`${request.receipts.length} Receipts`} size="small" />
                                        )}
                                        {request.photos && request.photos.length > 0 && (
                                            <Chip icon={<PhotoCamera />} label={`${request.photos.length} Photos`} size="small" />
                                        )}
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Requested: {new Date(request.requested_at).toLocaleString()}
                                        </Typography>
                                        <Box>
                                            {isOwner && request.status === 'pending' && (
                                                <>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() => {
                                                            setSelectedRequest(request);
                                                            setViewDialogOpen(true);
                                                        }}
                                                        sx={{ mr: 1 }}
                                                    >
                                                        Review
                                                    </Button>
                                                </>
                                            )}
                                            {!isOwner && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setSelectedRequest(request);
                                                        setViewDialogOpen(true);
                                                    }}
                                                >
                                                    <Visibility />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Create Request Dialog (PM only) */}
            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogCloseButton onClick={() => setCreateDialogOpen(false)} />
                <DialogTitle>Create Payment Request</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        type="number"
                        label="Amount (USD)"
                        value={newRequest.amount}
                        onChange={(e) => setNewRequest({ ...newRequest, amount: e.target.value })}
                        sx={{ mb: 2, mt: 1 }}
                        required
                    />
                    <TextField
                        fullWidth
                        select
                        label="Payment Type"
                        value={newRequest.paymentType}
                        onChange={(e) => setNewRequest({ ...newRequest, paymentType: e.target.value })}
                        SelectProps={{ native: true }}
                        sx={{ mb: 2 }}
                    >
                        <option value="material_purchase">Material Purchase</option>
                        <option value="contractor_payment">Contractor Payment</option>
                        <option value="milestone_release">Milestone Release</option>
                        <option value="verification_fee">Verification Fee</option>
                    </TextField>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Description"
                        value={newRequest.description}
                        onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
                        sx={{ mb: 2 }}
                        required
                    />
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Justification"
                        value={newRequest.justification}
                        onChange={(e) => setNewRequest({ ...newRequest, justification: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                        <Button component="label" variant="outlined" startIcon={<Receipt />} disabled={uploading}>
                            {uploading ? 'Uploading…' : 'Attach Receipts'}
                            <input hidden multiple accept="image/*,application/pdf" type="file"
                                onChange={(e) => handleFileUpload(e, 'receipts')} />
                        </Button>
                        <Button component="label" variant="outlined" startIcon={<PhotoCamera />} disabled={uploading}>
                            {uploading ? 'Uploading…' : 'Attach Photos'}
                            <input hidden multiple accept="image/*" type="file"
                                onChange={(e) => handleFileUpload(e, 'photos')} />
                        </Button>
                    </Box>
                    {(newRequest.receipts.length > 0 || newRequest.photos.length > 0) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            {newRequest.receipts.length} receipt(s), {newRequest.photos.length} photo(s) attached
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateRequest}
                        disabled={submitting || !newRequest.amount || !newRequest.description}
                    >
                        {submitting ? <CircularProgress size={20} /> : 'Submit Request'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View/Respond Dialog (Owner) */}
            <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogCloseButton onClick={() => setViewDialogOpen(false)} />
                <DialogTitle>
                    Payment Request - ${selectedRequest && parseFloat(selectedRequest.amount).toLocaleString()}
                </DialogTitle>
                <DialogContent>
                    {selectedRequest && (
                        <>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                <strong>Type:</strong> {selectedRequest.payment_type.replace('_', ' ')}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                <strong>Description:</strong> {selectedRequest.description}
                            </Typography>
                            {selectedRequest.justification && (
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    <strong>Justification:</strong> {selectedRequest.justification}
                                </Typography>
                            )}
                            {selectedRequest.status === 'pending' && (
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Response Notes (optional)"
                                    value={responseNotes}
                                    onChange={(e) => setResponseNotes(e.target.value)}
                                    sx={{ mt: 2 }}
                                />
                            )}
                            {selectedRequest.owner_notes && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    <strong>Owner Notes:</strong> {selectedRequest.owner_notes}
                                </Alert>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
                    {selectedRequest && selectedRequest.status === 'pending' && isOwner && (
                        <>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => handleRespond(selectedRequest.id, 'reject')}
                                disabled={submitting}
                            >
                                {submitting ? <CircularProgress size={20} /> : 'Reject'}
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={() => handleRespond(selectedRequest.id, 'approve')}
                                disabled={submitting}
                            >
                                {submitting ? <CircularProgress size={20} /> : 'Approve'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PMPaymentRequests;



